import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogComponent } from '@app/components';
import { InsurableEarningsOptions } from '@app/interfaces';
import { AuthService, NotifyService, PayrollService } from '@app/services';
import { ShowView } from '@common/views';
import { Roe } from '@models/payroll/roe.model';
import { InsurableEarning } from '@payroll/interfaces';
import * as Sentry from '@sentry/angular';
import format from 'date-fns/format';
import moment from 'moment';

const ROE_ISSUING_REASON_OPTIONS = [
    { value: 'A00', label: 'Shortage of work/End of contract or season' },
    { value: 'A01', label: 'Employer bankruptcy or receivership' },
    { value: 'B00', label: 'Strike or lockout' },
    { value: 'D00', label: 'Illness or injury' },
    { value: 'E00', label: 'Quit' },
    { value: 'E02', label: 'Quit/Follow spouse' },
    { value: 'E03', label: 'Quit/Return to school' },
    { value: 'E04', label: 'Quit/Health reasons' },
    { value: 'E05', label: 'Quit/Voluntary retirement' },
    { value: 'E06', label: 'Quit/Take another job' },
    { value: 'E09', label: 'Quit/Employer relocation' },
    { value: 'E10', label: 'Quit/Care for a dependant' },
    { value: 'E11', label: 'Quit/To become self-employed' },
    { value: 'F00', label: 'Maternity' },
    { value: 'G00', label: 'Mandatory retirement' },
    { value: 'G07', label: 'Retirement/Approved workforce reduction' },
    { value: 'H00', label: 'Work-Sharing' },
    { value: 'J00', label: 'Apprentice training' },
    { value: 'K00', label: 'Other' },
    { value: 'K12', label: 'Other/Change of payroll frequency' },
    { value: 'K13', label: 'Other/Change of ownership' },
    { value: 'K14', label: 'Other/Requested by Employment Insurance' },
    { value: 'K15', label: "Other/Canadian Forces - Queen's Regulations/Orders" },
    { value: 'K16', label: "Other/At the employee's request" },
    { value: 'K17', label: 'Other/Change of Service Provider' },
    { value: 'M00', label: 'Dismissal' },
    { value: 'M08', label: 'Dismissal/Terminated within probationary period' },
    { value: 'N00', label: 'Leave of absence' },
    { value: 'P00', label: 'Parental' },
    { value: 'Z00', label: 'Compassionate Care/Family Caregiver' },
];

const ROE_CONTACT_PLACEHOLDER_VALUES = {
    firstName: 'first',
    lastName: 'last',
    areaCode: '000',
    phoneNumber: '0000000',
    phoneExtensionNumber: '0000000',
};
@Component({
    templateUrl: './roe-update.template.html',
    styleUrls: ['./roe-update.style.scss'],
})
export class RoeUpdateView extends ShowView implements OnInit {
    @ViewChild('roeErrorModal') roeErrorModal!: DialogComponent;
    isLoading = true;

    isPlaceholderValues = false;
    roe?: Roe;

    private isEmployeeTerminatedOrOnLeave = true;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        protected auth: AuthService,
        private payrollService: PayrollService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.roe = await Roe.param('company', this.auth.company?.id)
            .with('employee')
            .find(this.route.snapshot.params.roe);

        await this.checkIfEmployeeIsTerminatedOrOnLeave(this.roe);

        this.checkIfPlaceholderValues();
        this.isLoading = false;
    }

    /**
     * If the employee is not on LOA or terminated, only return K## reason codes
     */
    get roeIssuingReasonOptions(): (typeof ROE_ISSUING_REASON_OPTIONS)[number][] {
        if (!this.isEmployeeTerminatedOrOnLeave) {
            return ROE_ISSUING_REASON_OPTIONS.filter(({ value }) => value[0] === 'K');
        }

        return ROE_ISSUING_REASON_OPTIONS;
    }

    getInsurableAmountByPayPeriodNumber(payPeriodNumber: number): InsurableEarning | null | undefined {
        return this.roe?.insurableEarnings.find((insurableEarning) => {
            return insurableEarning.payPeriodNumber === payPeriodNumber;
        });
    }

    onDelete(): void {}

    onCancel(): void {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    onSave(): void {
        if (!this.roe) return;

        this.isLoading = true;
        this.roe
            .save()
            .then(() => {
                this.notify.success('saved');
                this.router.navigate(['../'], { relativeTo: this.route });
            })
            .catch((err) => {
                err.message.includes('Total_insurable_hours')
                    ? (this.roeErrorModal.open = true)
                    : this.notify.error(err.message || 'Could not save');

                this.isLoading = false;
            });
    }

    toggleRecallDateValue(): void {
        if (!this.roe) return;

        if (!this.roe.recallCodeIsY()) {
            this.roe.expectedRecall.date = null;
        }
    }

    updateInsurableEarnings(date: string, firstDateWorked: boolean): void {
        if (!this.roe) return;

        const roe = this.roe;

        this.isLoading = true;
        const formattedDate = moment(date).format('YYYY-MM-DD');
        firstDateWorked ? (roe.firstDateWorked = formattedDate) : (roe.lastDateForWhichPaid = formattedDate);
        const options: InsurableEarningsOptions = {
            firstDateWorked: this.roe.firstDateWorked,
            lastDateForWhichPaid: this.roe.lastDateForWhichPaid,
        };

        this.payrollService
            .insurableEarnings(this.auth.company, this.roe, options)
            .then((response: any) => {
                this.notify.success('New set of Per-period Insurable Earnings for (Block 15C) generated.');
                roe.insurableEarnings = response.data[0].attributes['insurableEarnings'];
                roe.payPeriods = response.data[0].attributes['payPeriods'];
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'Could not generate Insurable earnings.');
                this.isLoading = false;
            });
    }

    checkIfPlaceholderValues(): void {
        if (!this.roe?.reasonForIssuing?.contact) return;

        const contact = this.roe.reasonForIssuing.contact;
        const fields = Object.entries(ROE_CONTACT_PLACEHOLDER_VALUES) as [
            keyof typeof ROE_CONTACT_PLACEHOLDER_VALUES,
            string,
        ][];

        this.isPlaceholderValues = fields.some(([field, placeholder]) => {
            const value = contact[field]?.toLowerCase();
            return value === placeholder.toLowerCase();
        });
    }

    /**
     * Check if the employee this ROE was issued to is terminated or on a leave of
     * absence, in which case we won't filter the reason codes
     */
    private async checkIfEmployeeIsTerminatedOrOnLeave(roe: Roe): Promise<void> {
        if (!this.auth.company) return;

        const payrollEmployee = roe.employee;

        if (!payrollEmployee) {
            Sentry.captureException('ROE does not have an attached payroll employee');

            return;
        }

        try {
            const { onLeaveOrTerminated } = await this.payrollService.checkEmployeeOnLeaveOrTerminated(
                this.auth.company,
                payrollEmployee,
                format(new Date(), 'YYYY-MM-DD')
            );

            this.isEmployeeTerminatedOrOnLeave = onLeaveOrTerminated;
        } catch (err) {
            Sentry.captureException(err);
        }
    }
}
